import { Row, RowSelectionState, Updater } from '@tanstack/react-table';

/**
 * Common global filter function for tables
 */

export const getGlobalFilterFn = <TData>() => {
  return (row: Row<TData>, _: unknown, filterValue: string) => {
    if (!filterValue) return true;

    const value = String(filterValue).toLowerCase();
    const rowData = row.original as Record<string, unknown>;

    return Object.entries(rowData).some(([, cellValue]) => {
      if (cellValue == null) return false;

      switch (typeof cellValue) {
        case 'string':
          return cellValue.toLowerCase().includes(value);
        case 'number':
          return cellValue.toString().includes(value);
        case 'boolean': {
          const boolString = cellValue.toString().toLowerCase();
          return (
            boolString.includes(value) ||
            (cellValue === true && value === 'yes') ||
            (cellValue === false && value === 'no')
          );
        }
        default:
          if (cellValue instanceof Date) {
            const dateString = cellValue.toISOString().toLowerCase();
            return dateString.includes(value);
          }
          try {
            return JSON.stringify(cellValue).toLowerCase().includes(value);
          } catch {
            return false;
          }
      }
    });
  };
};

export const booleanFilterFn = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string,
) => {
  const value = String(filterValue).toLowerCase().trim();
  const cellValue = (row.original as Record<string, unknown>)[
    columnId
  ] as boolean;

  if ('yes'.includes(value) || 'true'.includes(value)) {
    return cellValue === true;
  }

  if ('no'.includes(value) || 'false'.includes(value)) {
    return cellValue === false;
  }

  return false;
};

/**
 * Default get row id function
 */
export const getDefaultRowId = <TData>(row: TData): string => {
  return (row as { id: number }).id?.toString();
};

/**
 * Handle row selection change
 */
export const handleRowSelectionChange = (
  updater: Updater<RowSelectionState>,
  rowSelection: RowSelectionState | undefined,
  setRowSelection?: (rowSelection: RowSelectionState) => void,
) => {
  if (!setRowSelection) return;

  if (typeof updater === 'function') {
    setRowSelection(updater(rowSelection ?? {}));
  } else {
    setRowSelection(updater);
  }
};
